import { SignInButton, useAuth, useUser } from "@clerk/react"
import {
  BarChart3,
  CircleUserRound,
  Folder,
  Menu,
  MoonStar,
  PanelLeftClose,
  PanelLeftOpen,
  Shield,
  SunMedium,
  Swords,
} from "lucide-react"
import type { MouseEvent } from "react"
import { useEffect, useRef, useState } from "react"
import { useRemoteAccessDecision } from "../auth/useRemoteAccess"
import { FriendsPage } from "../friends/FriendsPage"
import { useStoredState } from "../hooks/useStoredState"
import { useI18n } from "../i18n/useI18n"
import { ManagePage } from "../manage/ManagePage"
import { MatchPage } from "../match/MatchPage"
import { ProfilePresenceHeartbeat } from "../profile/ProfilePresenceHeartbeat"
import {
  EMPTY_PLAYER_PROFILE_STORE,
  PLAYER_PROFILES_STORAGE_KEY,
  PLAYER_PROFILES_STORAGE_VERSION,
  PlayerProfileStoreStateSchema,
} from "../profile/storage"
import { getProfileByEmail, normalizeEmail } from "../profile/validation"
import {
  APP_NAV_GROUPS,
  type AppNavGroupId,
  type MainNavGroupId,
} from "../router/routes"
import { useAppRouter } from "../router/useAppRouter"
import { AccountPage, AdminPage } from "../settings/SettingsPage"
import { RouteErrorBoundary } from "./RouteErrorBoundary"

type Theme = "dark" | "light"
const THEME_KEY = "craft-force.theme"

function getInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_KEY)
    if (stored === "dark" || stored === "light") return stored
  } catch {
    // ignore
  }
  return window.matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark"
}

function StatsPage() {
  const { t } = useI18n()

  return (
    <section className="panel">
      <h2>{t("stats.title")}</h2>
      <p className="meta">{t("stats.body")}</p>
    </section>
  )
}

function NotFoundPage({ pathname }: { pathname: string }) {
  const { t } = useI18n()

  return (
    <section className="panel panel-danger">
      <h2>{t("notFound.title")}</h2>
      <p>{t("notFound.body", { pathname })}</p>
    </section>
  )
}

function renderRoute(id: string, navGroup: AppNavGroupId, pathname: string) {
  switch (navGroup) {
    case "play":
      if (id === "not_found") return <NotFoundPage pathname={pathname} />
      if (id === "match") return <MatchPage />
      return <FriendsPage />
    case "manage":
      return <ManagePage />
    case "stats":
      return <StatsPage />
    case "account":
      return <AccountPage />
    case "admin":
      return <AdminPage />
  }
}

function NavToggle({
  isOpen,
  onToggle,
}: {
  isOpen: boolean
  onToggle: () => void
}) {
  const { t } = useI18n()
  const Icon = isOpen ? PanelLeftClose : PanelLeftOpen
  return (
    <button
      aria-expanded={isOpen}
      aria-label={isOpen ? t("nav.toggle.collapse") : t("nav.toggle.expand")}
      className="nav-toggle"
      onClick={onToggle}
      type="button"
    >
      <Icon size={20} strokeWidth={2} />
    </button>
  )
}

function NavUserArea({
  isOpen,
  isActive,
  onNavigate,
}: {
  isOpen: boolean
  isActive: boolean
  onNavigate: () => void
}) {
  const { isLoaded, userId } = useAuth()
  const { user } = useUser()
  const { t } = useI18n()
  const [profiles] = useStoredState(
    PLAYER_PROFILES_STORAGE_KEY,
    PlayerProfileStoreStateSchema,
    {
      defaultValue: EMPTY_PLAYER_PROFILE_STORE,
      version: PLAYER_PROFILES_STORAGE_VERSION,
    },
  )

  if (!isLoaded) return null

  if (!userId) {
    return (
      <div className="nav-user-area">
        <SignInButton mode="modal">
          <button className="nav-sign-in" type="button">
            {isOpen ? t("nav.signIn") : t("nav.signInShort")}
          </button>
        </SignInButton>
      </div>
    )
  }

  const email = user?.primaryEmailAddress?.emailAddress ?? null
  const profile = email
    ? getProfileByEmail(profiles, normalizeEmail(email))
    : null
  const accountLabel =
    user?.fullName?.trim() ||
    [user?.firstName?.trim(), user?.lastName?.trim()]
      .filter(Boolean)
      .join(" ") ||
    user?.primaryEmailAddress?.emailAddress ||
    t("account.nav.defaultName")

  return (
    <div className="nav-user-area">
      <button
        aria-current={isActive ? "page" : undefined}
        className="nav-account-link"
        onClick={onNavigate}
        type="button"
      >
        {user?.imageUrl ? (
          <img
            alt=""
            className="nav-account-avatar"
            height={36}
            src={user.imageUrl}
            width={36}
          />
        ) : (
          <span className="nav-account-avatar nav-account-avatar-fallback">
            <CircleUserRound size={20} strokeWidth={2} />
          </span>
        )}
        {isOpen ? (
          <span className="nav-account-copy">
            <span className="nav-account-name">{accountLabel}</span>
            {profile?.gamerTag ? (
              <span className="nav-account-tag">{profile.gamerTag}</span>
            ) : null}
          </span>
        ) : null}
      </button>
    </div>
  )
}

export function AppShell() {
  const { t } = useI18n()
  const router = useAppRouter()
  const [isNavOpen, setIsNavOpen] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const [theme, setTheme] = useState<Theme>(getInitialTheme)
  const navRef = useRef<HTMLElement>(null)
  const firstLinkRef = useRef<HTMLAnchorElement>(null)

  // Apply theme to <html> and persist
  useEffect(() => {
    if (theme === "light") {
      document.documentElement.setAttribute("data-theme", "light")
    } else {
      document.documentElement.removeAttribute("data-theme")
    }
    try {
      localStorage.setItem(THEME_KEY, theme)
    } catch {
      // ignore
    }
  }, [theme])

  // Sync theme across tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (
        e.key === THEME_KEY &&
        (e.newValue === "dark" || e.newValue === "light")
      ) {
        setTheme(e.newValue)
      }
    }
    window.addEventListener("storage", handler)
    return () => window.removeEventListener("storage", handler)
  }, [])

  // Detect mobile breakpoint
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 48rem)")
    const update = (e: MediaQueryListEvent | MediaQueryList) => {
      const mobile = e.matches
      setIsMobile(mobile)
      if (mobile) setIsNavOpen(false)
      else setIsNavOpen(true)
    }
    update(mq)
    mq.addEventListener("change", update)
    return () => mq.removeEventListener("change", update)
  }, [])

  // Close mobile nav on route change
  const prevPathname = useRef(router.pathname)
  useEffect(() => {
    if (prevPathname.current !== router.pathname && isMobile) {
      setIsNavOpen(false)
    }
    prevPathname.current = router.pathname
  }, [router.pathname, isMobile])

  // Focus first link when mobile nav opens
  useEffect(() => {
    if (isMobile && isNavOpen) firstLinkRef.current?.focus()
  }, [isMobile, isNavOpen])

  // Close on Escape
  useEffect(() => {
    if (!isMobile || !isNavOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsNavOpen(false)
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [isMobile, isNavOpen])

  const handleNavClick = (e: MouseEvent<HTMLAnchorElement>, path: string) => {
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.altKey ||
      e.ctrlKey ||
      e.metaKey ||
      e.shiftKey
    )
      return
    e.preventDefault()
    router.navigate(path)
  }

  const { access } = useRemoteAccessDecision()
  const navIcons: Record<MainNavGroupId, typeof Swords> = {
    play: Swords,
    manage: Folder,
    stats: BarChart3,
    admin: Shield,
  }
  const ThemeIcon = theme === "dark" ? SunMedium : MoonStar
  const getNavLabel = (groupId: MainNavGroupId) =>
    t(`nav.${groupId}.label` as const)
  const getNavShortLabel = (groupId: MainNavGroupId) =>
    t(`nav.${groupId}.short` as const)

  const navGroups = APP_NAV_GROUPS.filter(
    (group) => group.id !== "admin" || access === "bootstrap_admin",
  )

  return (
    <div className="shell-layout">
      <ProfilePresenceHeartbeat />

      {/* Mobile backdrop */}
      {isMobile && isNavOpen ? (
        <button
          aria-label={t("nav.close")}
          className="nav-backdrop"
          onClick={() => setIsNavOpen(false)}
          type="button"
        />
      ) : null}

      {/* Mobile open button — only when nav is closed on mobile */}
      {isMobile && !isNavOpen ? (
        <button
          aria-label={t("nav.open")}
          className="nav-open-btn"
          onClick={() => setIsNavOpen(true)}
          type="button"
        >
          <Menu size={22} strokeWidth={2} />
        </button>
      ) : null}

      <nav
        aria-label={t("nav.primary")}
        className={[
          "global-nav",
          isMobile ? "global-nav-mobile" : null,
          isMobile && isNavOpen ? "global-nav-open" : null,
          !isMobile && !isNavOpen ? "global-nav-collapsed" : null,
        ]
          .filter(Boolean)
          .join(" ")}
        ref={navRef}
      >
        {/* Toggle row: hamburger + theme toggle (visible when open) */}
        <div className="nav-toggle-row">
          <NavToggle
            isOpen={isNavOpen}
            onToggle={() => setIsNavOpen((v) => !v)}
          />
          {isNavOpen ? (
            <button
              aria-label={
                theme === "dark" ? t("nav.theme.light") : t("nav.theme.dark")
              }
              className="nav-theme-btn"
              onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
              type="button"
            >
              <ThemeIcon size={18} strokeWidth={2} />
            </button>
          ) : null}
        </div>

        {/* Nav links */}
        <div className="nav-links">
          {navGroups.map((group, i) => {
            const isActive = router.navGroup === group.id
            const Icon = navIcons[group.id]
            return (
              <a
                key={group.id}
                aria-current={isActive ? "page" : undefined}
                className="nav-link"
                href={group.path}
                onClick={(e) => handleNavClick(e, group.path)}
                ref={i === 0 ? firstLinkRef : undefined}
              >
                <span className="nav-link-icon" aria-hidden="true">
                  <Icon size={18} strokeWidth={2} />
                </span>
                {isNavOpen ? (
                  <span className="nav-link-label">
                    {getNavLabel(group.id)}
                  </span>
                ) : (
                  <span className="nav-link-label">
                    {getNavShortLabel(group.id)}
                  </span>
                )}
              </a>
            )
          })}
        </div>

        {/* Bottom: user + app name */}
        <div className="nav-footer">
          <NavUserArea
            isActive={router.navGroup === "account"}
            isOpen={isNavOpen}
            onNavigate={() => router.navigate("/account")}
          />
          {isNavOpen ? <p className="nav-app-name">{t("app.name")}</p> : null}
        </div>
      </nav>

      <main className="shell-content">
        <RouteErrorBoundary key={router.id}>
          {renderRoute(router.id, router.navGroup, router.pathname)}
        </RouteErrorBoundary>
      </main>
    </div>
  )
}
